import { Injectable } from '@angular/core';
import { LoginService } from './pages/service/login.service';

const TOKEN_KEY = 'bpv_auth_token';
const USER_KEY  = 'bpv_auth_user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _isAuthenticated = false;
  private _userRole = '';
  private _userName = '';

  constructor() {
    this.restoreSession();
  }

  // ── Session persistence ─────────────────────────────────────────────────

  private restoreSession(): void {
    const token = localStorage.getItem(TOKEN_KEY);
    const user  = localStorage.getItem(USER_KEY);
    if (token && user && !this.isTokenExpired(token)) {
      try {
        const parsed = JSON.parse(user);
        this._isAuthenticated = true;
        this._userRole = parsed.role      ?? '';
        this._userName = parsed.user_name ?? '';
      } catch {
        this.clearSession();
      }
    } else {
      this.clearSession();
    }
  }

  private clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._isAuthenticated = false;
    this._userRole = '';
    this._userName = '';
  }

  // ── Token helpers ───────────────────────────────────────────────────────

  /** Decode the exp claim from a JWT without verifying the signature. */
  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      // base64url → base64 → JSON
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return (payload.exp ?? 0) * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && !this.isTokenExpired(token)) {
      return token;
    }
    // Token expired – clean up silently
    this.clearSession();
    return null;
  }

  // ── Auth actions ────────────────────────────────────────────────────────

  login(loginService: LoginService, user: string, pass: string): Promise<boolean> {
    return new Promise((resolve) => {
      loginService.getUserStatus(user, pass).subscribe({
        next: (authItem) => {
          if (authItem.user_name === user && authItem.auth === true && authItem.token) {
            this._isAuthenticated = true;
            this._userRole = authItem.user_group_name ?? '';
            this._userName = authItem.user_name ?? '';
            localStorage.setItem(TOKEN_KEY, authItem.token);
            localStorage.setItem(USER_KEY, JSON.stringify({
              user_name: authItem.user_name,
              role:      authItem.user_group_name,
            }));
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: () => resolve(false),
      });
    });
  }

  /**
   * Establish a session directly from a JWT (e.g. returned by the Discord
   * OAuth2 callback). Returns true if the token is valid and a session was set.
   */
  loginWithToken(token: string): boolean {
    if (!token || this.isTokenExpired(token)) {
      return false;
    }
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      this._isAuthenticated = true;
      this._userRole = payload.role      ?? '';
      this._userName = payload.user_name ?? '';
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify({
        user_name: payload.user_name,
        role:      payload.role,
      }));
      return true;
    } catch {
      return false;
    }
  }

  logout(): void {
    this.clearSession();
  }

  // ── Accessors ───────────────────────────────────────────────────────────

  /** True if a valid, non-expired token exists in storage. */
  isLoggedIn(): boolean {
    return this.getToken() !== null;
  }

  getUserRole(): string {
    return this._userRole;
  }

  getUserName(): string {
    return this._userName;
  }
}
